import { ROLE_DESCRIPTIONS, ROLE_LABELS, type Member, type Workspace } from '@unitly/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet } from '../../ui/Sheet.tsx';
import { PageLoader, WorkspaceAvatar } from '../../ui/primitives.tsx';
import { useMemberWorkspaces, useWorkspaceMember, useWorkspaces } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { useToast } from '../../ui/Toast.tsx';

function WorkspaceAccessRow({ orgId, workspace, memberId, memberName, isIn }: {
  orgId: string; workspace: Workspace; memberId: string; memberName: string; isIn: boolean;
}) {
  const mutate = useWorkspaceMember(orgId, workspace.id);
  const qc = useQueryClient();
  const toast = useToast();
  return (
    <div className="section-row">
      <WorkspaceAvatar name={workspace.name} keyLabel={workspace.key} color={workspace.color} logoUrl={workspace.logoUrl} size={32} />
      <span className="grow">{workspace.name}</span>
      <button
        type="button" role="switch" className="switch" aria-checked={isIn}
        aria-label={isIn ? `Togli ${memberName} da ${workspace.name}` : `Aggiungi ${memberName} a ${workspace.name}`}
        disabled={mutate.isPending}
        onClick={() => mutate.mutate({ userId: memberId, remove: isIn }, {
          onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['member-workspaces', orgId, memberId] });
            toast(isIn ? `${memberName} rimosso da ${workspace.name}` : `${memberName} aggiunto a ${workspace.name}`);
          },
          onError: (e) => toast(e.message),
        })}
      />
    </div>
  );
}

/** What an invited collaborator can and cannot see, in one place: their role (the kind of access) and
 * which workspaces they're actually in (the scope of it) — the two things that together decide visibility. */
export function MemberAccessSheet({ open, onClose, member }: { open: boolean; onClose: () => void; member: Member | null }) {
  const { orgId } = useOrgCtx();
  const workspaces = useWorkspaces(orgId);
  const memberWorkspaces = useMemberWorkspaces(orgId, open ? member?.userId ?? null : null);
  if (!member) return null;
  const inIds = new Set(memberWorkspaces.data?.workspaceIds ?? []);

  return (
    <Sheet open={open} onClose={onClose} title="Cosa può vedere" variant="center" labelledBy="access-title">
      <h2 id="access-title" className="sr-only">Accesso di {member.displayName}</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <span style={{ fontWeight: 600, fontSize: 17 }}>{member.displayName}</span>
        <span className="pill">{ROLE_LABELS[member.role]}</span>
      </div>
      <p className="hint" style={{ marginTop: 0 }}>{ROLE_DESCRIPTIONS[member.role]}</p>

      <h3 className="subhead">Spazi</h3>
      <p className="hint" style={{ marginTop: 0 }}>Vede solo gli spazi qui sotto attivati; dentro ognuno, quanto vede dipende dal ruolo qui sopra.</p>
      {workspaces.isLoading || memberWorkspaces.isLoading ? <PageLoader /> : (
        <div className="card list">
          {workspaces.data?.map((w) => (
            <WorkspaceAccessRow key={w.id} orgId={orgId} workspace={w} memberId={member.userId} memberName={member.displayName} isIn={inIds.has(w.id)} />
          ))}
          {workspaces.data?.length === 0 && <p className="hint" style={{ padding: 16, margin: 0 }}>Nessuno spazio ancora.</p>}
        </div>
      )}
    </Sheet>
  );
}
